import React from 'react';
import './SheetImposition.css';

/**
 * Renderiza el pliego SIEMPRE en orientación horizontal (acostado).
 * Si sheetHeight > sheetWidth, aplica rotación 90° antihoraria para dibujar horizontal.
 * Las coordenadas de las piezas (calculadas en portrait) se transforman a la vista landscape.
 *
 * Props:
 *  - sheetWidth, sheetHeight (cm) — dimensiones reales del pliego
 *  - gripperTop, gripperBottom, marginLeft, marginRight (cm)
 *  - pieces: array de { x, y, w, h, rotated } — en sistema portrait original
 *  - itemWidth, itemHeight (cm) — dimensiones de la pieza real (sin sangrado)
 *  - bleed (cm)
 *  - side: 'front' | 'back'
 *  - maxWidth: ancho máximo del SVG en px
 */
const SheetImposition = ({
  sheetWidth = 52,
  sheetHeight = 74,
  gripperTop = 2,
  gripperBottom = 0,
  marginLeft = 0,
  marginRight = 0,
  pieces = [],
  itemWidth = 0,
  itemHeight = 0,
  bleed = 0,
  side = 'front',
  maxWidth = 520,
  showLegend = true,
}) => {
  // Si el pliego es "vertical" (alto > ancho), rotamos 90° antihorario para mostrarlo acostado
  const needsRotation = sheetHeight > sheetWidth;

  // Función que transforma (x, y, w, h) portrait → landscape (rotación 90° antihoraria)
  const transform = (x, y, w, h) => {
    if (!needsRotation) return { x, y, w, h };
    // Rotación 90° antihoraria alrededor del origen:
    //   nuevo_x = y
    //   nuevo_y = sheetWidth_original - x - w
    //   nuevo_w = h
    //   nuevo_h = w
    return {
      x: y,
      y: sheetWidth - x - w,
      w: h,
      h: w,
    };
  };

  // Dimensiones visuales
  const visualW = needsRotation ? sheetHeight : sheetWidth;
  const visualH = needsRotation ? sheetWidth : sheetHeight;

  // Escalar para caber en maxWidth
  const scale = maxWidth / visualW;
  const svgW = visualW * scale;
  const svgH = visualH * scale;

  // Área imprimible (en sistema portrait)
  const printableX0 = marginLeft;
  const printableY0 = gripperTop;
  const printableW = sheetWidth - marginLeft - marginRight;
  const printableH = sheetHeight - gripperTop - gripperBottom;
  const printable = transform(printableX0, printableY0, printableW, printableH);

  // Zona de pinza superior (portrait) → después de rotación queda a la izquierda
  const gripperTopBox = gripperTop > 0
    ? transform(0, 0, sheetWidth, gripperTop)
    : null;
  const gripperBottomBox = gripperBottom > 0
    ? transform(0, sheetHeight - gripperBottom, sheetWidth, gripperBottom)
    : null;

  // Piezas transformadas
  const drawn = pieces.map(p => {
    const t = transform(p.x, p.y, p.w, p.h);
    // Para el dorso: espejar horizontalmente sobre el ancho visual
    if (side === 'back') {
      t.x = visualW - t.x - t.w;
    }
    return { ...t, origW: p.w, origH: p.h, rotated: p.rotated };
  });

  // Colores
  const pieceFill = side === 'back' ? '#DBEAFE' : '#D1FAE5'; // azul claro / verde claro
  const pieceStroke = side === 'back' ? '#2563EB' : '#059669';
  const gripperFill = '#111111';
  const gripperOpacity = 0.15;

  // Fuente para etiquetas dentro de piezas
  const labelFontCm = Math.min(...drawn.map(p => Math.min(p.w, p.h) * 0.18), 3);

  return (
    <div className="sheet-container" data-testid={`sheet-preview-${side}`}>
      <div className="sheet-label">
        {side === 'back' ? 'DORSO (espejado)' : 'FRENTE'} · Pliego {sheetWidth} × {sheetHeight} cm · {pieces.length} piezas
      </div>

      <svg
        width={svgW}
        height={svgH}
        viewBox={`0 0 ${visualW} ${visualH}`}
        className="sheet-svg"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Pliego (fondo blanco con borde) */}
        <rect x={0} y={0} width={visualW} height={visualH} fill="#FFFFFF" stroke="#111111" strokeWidth={0.2} />

        {/* Área imprimible (verde claro de fondo = desperdicio visible al restar las piezas) */}
        <rect
          x={printable.x}
          y={printable.y}
          width={printable.w}
          height={printable.h}
          fill="#FEF2F2"
          stroke="#DC2626"
          strokeWidth={0.15}
          strokeDasharray="0.6,0.4"
        />

        {/* Piezas */}
        {drawn.map((p, idx) => {
          const showLabel = p.w >= 4 && p.h >= 3;
          return (
            <g key={idx}>
              <rect
                x={p.x}
                y={p.y}
                width={p.w}
                height={p.h}
                fill={pieceFill}
                stroke={pieceStroke}
                strokeWidth={0.2}
              />
              {showLabel && (
                <text
                  x={p.x + p.w / 2}
                  y={p.y + p.h / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={labelFontCm}
                  fill={pieceStroke}
                  fontFamily="'IBM Plex Mono', monospace"
                  fontWeight="600"
                >
                  {itemWidth} × {itemHeight}
                </text>
              )}
            </g>
          );
        })}

        {/* Pinzas (por encima de las piezas para que se vea la zona no imprimible) */}
        {gripperTopBox && (
          <rect
            x={gripperTopBox.x}
            y={gripperTopBox.y}
            width={gripperTopBox.w}
            height={gripperTopBox.h}
            fill={gripperFill}
            fillOpacity={gripperOpacity}
          />
        )}
        {gripperBottomBox && (
          <rect
            x={gripperBottomBox.x}
            y={gripperBottomBox.y}
            width={gripperBottomBox.w}
            height={gripperBottomBox.h}
            fill={gripperFill}
            fillOpacity={gripperOpacity}
          />
        )}
      </svg>

      {/* Etiquetas de dimensiones */}
      <div className="sheet-dimensions" style={{ width: svgW }}>
        <span>{visualW} cm</span>
        <span>·</span>
        <span>{visualH} cm de alto</span>
      </div>

      {showLegend && (
        <div className="sheet-legend">
          <div className="legend-item">
            <span className="legend-swatch" style={{ background: pieceFill, borderColor: pieceStroke }}></span>
            Pieza
          </div>
          <div className="legend-item">
            <span className="legend-swatch legend-waste"></span>
            Área desperdiciada
          </div>
          <div className="legend-item">
            <span className="legend-swatch legend-gripper"></span>
            Pinza (no imprimible)
          </div>
        </div>
      )}
    </div>
  );
};

export default SheetImposition;
